'use client';

import { useRef, useState, type RefObject } from 'react';
import { styled, createGlobalStyle } from 'styled-components';
import { Icon } from '@wistia/ui';
import cs from 'classnames';
import {
  MENU_DATA,
  TRANSITION_DURATION,
  BREAKPOINT_DRAWER,
} from '@content/navigation/navData';
import MobileMenu from '@content/navigation/MobileMenu';
import { cohortMenuStyles } from '@content/navigation/cohort-menu-styles';
import SalesCta from '@content/navigation/SalesCta';
import AccountCtas from '@content/navigation/AccountCtas';
import { MOBILE_NAVBAR_OFFSET_DEFAULT } from '@lib/constants';
import NavbarLogo from '@content/navigation/NavbarLogo';
import SimplifiedMenu from '@content/navigation/simplified/menu';
import {
  MENU_DATA as SIMPLIFIED_MENU_DATA,
  navMotionVars,
} from '@content/navigation/simplified/nav-data';

type MobileNavbarProps = {
  variant?: 'control' | 'test';
  /**
   * The nav bar the drawer hangs from. The drawer is fixed at the nav’s
   * height, which only lines up while the nav sits at the top of the
   * viewport, so opening the drawer scrolls the page until it is. That covers
   * anything rendered above the nav (the homepage promo) and viewports too
   * short for the nav to be sticky.
   */
  navRef: RefObject<HTMLElement | null>;
};

const BodyLock = createGlobalStyle`
  html {
    overflow: hidden;

    @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
      overflow: revert;
    }
  }
`;

const NavButton = styled.button`
  background: none;
  border: unset;
  color: ${({ theme }) => theme.colors.grey600};
  cursor: pointer;
  margin-inline-start: auto;
  // iOS Safari can trigger a slight movement of fixed elements. Force hardware acceleration with the following:
  transform: translateZ(0);
  will-change: transform;

  &:hover,
  &:focus-visible,
  &:active {
    color: var(--global-hover-color);
  }

  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    display: none;
  }
`;

const NavBar = styled.div`
  ${navMotionVars}
  background: ${({ theme }) => theme.colors.blue100};
  bottom: 0;
  box-shadow: inset 0 1px 0 0 ${({ theme }) => theme.colors.blue200};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow-y: scroll;

  /** custom per design */
  padding: 1.875rem;
  padding-block-start: 0;
  position: fixed;
  right: 0;
  top: ${MOBILE_NAVBAR_OFFSET_DEFAULT}px;
  transform: translateX(100%);
  transition: transform ${TRANSITION_DURATION} linear;

  &.simplified {
    left: 0;
    overflow-y: auto;
    --nav-drawer-duration: 320ms;
    --nav-drawer-ease: cubic-bezier(0.65, 0, 0.35, 1);
    visibility: hidden;
    transition:
      transform var(--nav-drawer-duration) var(--nav-drawer-ease),
      visibility 0s linear var(--nav-drawer-duration);

    &.isOpen {
      visibility: visible;
      transition-delay: 0s;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &.simplified {
      transition: none;
    }
  }

  &.isOpen {
    transform: translateX(0);
  }

  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    display: none;

    &.simplified {
      align-items: center;
      align-self: stretch;
      background: none;
      box-shadow: none;
      display: flex;
      flex: 1;
      flex-direction: row;
      overflow: visible;
      padding: 0;
      position: static;
      transform: none;
      transition: none;
      visibility: visible;
    }
  }
`;

const Menus = styled.ul`
  ${cohortMenuStyles}
  align-self: stretch;
  display: grid;
  list-style: none;
  margin: 0;
  padding: 0;

  padding-block-start: ${({ theme }) => theme.spacing[0.75]};

  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    align-self: stretch;
    grid-auto-flow: column;
    padding: 0;
    padding-inline-start: ${({ theme }) => theme.spacing[0.5]};
  }
`;

const CtaWrapper = styled.div`
  align-items: center;
  display: grid;
  gap: ${({ theme }) => theme.spacing[1]};
  justify-items: center;
  width: 100%;

  @media screen and (min-width: ${BREAKPOINT_DRAWER}) {
    display: none;
  }
`;

const MobileNavbar = ({ navRef, variant = 'control' }: MobileNavbarProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuIcon = isDrawerOpen ? 'close' : 'menu';

  const handleToggleDrawer = () => {
    // TODO: revisit with the next navigation. Once CSS anchor positioning is
    // supported across our browsers, the drawer can anchor to the nav
    // (`position-anchor` + `top: anchor(bottom)`) and this scroll goes away.
    // Not scrollIntoView: Chrome treats a sticky target as already in view.
    // Instant, not the root’s smooth scroll: the body lock that follows this
    // render would cut a smooth scroll short.
    if (!isDrawerOpen && navRef.current) {
      window.scrollBy({
        top: navRef.current.getBoundingClientRect().top,
        behavior: 'instant',
      });
    }
    setIsDrawerOpen(!isDrawerOpen);
  };

  const handleCloseDrawer = () => {
    if (!isDrawerOpen) return;
    setIsDrawerOpen(false);
  };

  return (
    <>
      {/* NavbarLogo lives here to handle closing the drawer on mobile. */}
      <NavbarLogo onLogoClick={handleCloseDrawer} />
      <NavButton
        ref={buttonRef}
        aria-expanded={isDrawerOpen}
        onKeyDown={(event) => {
          if (event.key === 'Escape') handleCloseDrawer();
        }}
        aria-label="navigation menu"
        onClick={handleToggleDrawer}
        tabIndex={0}
        type="button"
      >
        <Icon size="lg" type={menuIcon} />
      </NavButton>
      <NavBar
        className={cs({ isOpen: isDrawerOpen, simplified: variant === 'test' })}
        inert={variant === 'control' ? !isDrawerOpen : undefined}
        onKeyDown={(event) => {
          if (
            event.key === 'Escape' &&
            !window.matchMedia(`(min-width: ${BREAKPOINT_DRAWER})`).matches
          ) {
            handleCloseDrawer();
            buttonRef.current?.focus();
          }
        }}
      >
        {isDrawerOpen ? <BodyLock /> : null}
        <Menus role="list">
          {variant === 'test'
            ? SIMPLIFIED_MENU_DATA.map((section) => (
                <SimplifiedMenu
                  key={section.id}
                  menu={section}
                  onMenuClick={handleCloseDrawer}
                />
              ))
            : MENU_DATA.map((section) => (
                <MobileMenu
                  key={section.id}
                  menu={section}
                  onMenuClick={handleCloseDrawer}
                />
              ))}
        </Menus>
        <CtaWrapper>
          <SalesCta />
          <AccountCtas handleCloseDrawer={handleCloseDrawer} />
        </CtaWrapper>
      </NavBar>
    </>
  );
};

export default MobileNavbar;
